import { useCallback, useEffect, useRef, useState } from 'react';
import { authService } from '@/lib/authService';
import { checkDuplicateEvidence, type DuplicateMatch } from './duplicateEvidence';
import {
  analyzeReceiptImages,
  matchReceiptSuggestionOption,
  normalizeReceiptPaymentMethod,
  type ReceiptAiPayload,
  type ReceiptAiValidation,
  type ReceiptCaptureProfile,
} from '@/lib/receiptAiAnalysis';
import { detectVatTreatment } from './taxResolution';
import {
  detectDuplicateLineItems,
  deduplicateLineItems,
  type DetectDuplicateLineItemsResult,
} from './lineItemDedup';
import {
  EXPENSE_ENTRY_TYPES,
  ENTRY_CONFIG,
  getReceiptCategoryMismatchInfo,
  PURCHASE_CATEGORY_OPTIONS,
} from './constants';
import type { CaptureFormData, CaptureCapabilities } from './captureSchema';
import { toRecordString } from './captureSchema';
import type { Supplier, PurchaseOrder, PoLineItem } from './useProcurementSearch';
import type { ChildEntryType } from '../../types';
import type { DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import type { DocumentTaxRate } from '@/components/finance/modals/SortableLineItem';
import notify from '@/lib/notifications';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { supplierNamesMatch, isSupplierDocumentEntryType } from '@/lib/finance/supplierNameMatch';

const logger = createLogger('useReceiptAnalysis');

export interface UseReceiptAnalysisParams {
  // Capture session state (from useCaptureSession)
  formData: CaptureFormData;
  setFormData: React.Dispatch<React.SetStateAction<CaptureFormData>>;
  setReceiptResult: React.Dispatch<React.SetStateAction<ReceiptAiPayload | null>>;
  setReceiptValidation: React.Dispatch<React.SetStateAction<ReceiptAiValidation | null>>;
  detectedCurrency: string;

  // Line items
  setExpenseLineItems: React.Dispatch<React.SetStateAction<DocumentLineItem[]>>;

  // Procurement context
  selectedEntryType: ChildEntryType | null;
  isProcurementType: boolean;
  selectedSupplier: Supplier | null;
  setSelectedSupplier: React.Dispatch<React.SetStateAction<Supplier | null>>;
  selectedPurchaseOrder: PurchaseOrder | null;
  poLineItems: PoLineItem[];
  allSuppliers: Supplier[];

  // Environment context
  isOnline: boolean;
  preferredDefaultTaxRate: DocumentTaxRate | null;
  effectiveVehicleRegistration: string | null | undefined;
  linkableWorkItemLocation: string | null | undefined;
  captureProfile: ReceiptCaptureProfile;
  configFields: Array<{ key: string; options?: string[] }>;

  // Capability flags (from ChildEntryForm capabilities)
  canSwitchEntryTypeInCamera: boolean;

  /** When true, AI extraction fills form fields but never re-categorizes:
   * no entry-type auto-switch and no silent supplier auto-link. Used whenever
   * the technician selected a categorization — any of the 6 linking modes
   * (supplier / job / both / general / vehicle / wages), a vehicle-locked
   * capture, or a job-linked expense. A user selection is authoritative: AI
   * disagreement is recorded via `aiCategoryDisagreement` and flagged for
   * review on the DIN, but it must never override the selection. */
  suppressAutoCategorize?: boolean;

  // Auto-switch callback: called when AI detects a different document type
  onAutoSwitchEntryType?: (newType: ChildEntryType) => void;

  /** When set, routes AI analysis through the public token-authenticated endpoint and skips auth-dependent supplier matching */
  publicUploadToken?: string | null;
}

export interface AutoSwitchBanner {
  detectedType: ChildEntryType;
  detectedLabel: string;
  originalType: ChildEntryType;
}

/**
 * Records an AI-vs-technician categorization disagreement when the selected
 * categorization is locked (suppressAutoCategorize). The technician's
 * selection is kept; this is persisted on the DIN for admin review.
 */
export interface AiCategoryDisagreement {
  /** The entry type the technician's selection resolved to. */
  selectedType: ChildEntryType | null;
  /** The entry type the AI would have switched to. Null when the AI's
   *  suggestion doesn't map to a concrete type (e.g. 'entertainment'). */
  detectedType: ChildEntryType | null;
  detectedLabel: string | null;
  suggestedCategory: string | null;
  documentType: string | null;
  confidence: number | null;
}

export interface UseReceiptAnalysisReturn {
  runReceiptAnalysis: (images: string[]) => Promise<void>;

  // UI state
  receiptValidating: boolean;
  setReceiptValidating: React.Dispatch<React.SetStateAction<boolean>>;
  receiptError: string | null;
  setReceiptError: React.Dispatch<React.SetStateAction<string | null>>;
  receiptCategoryWarning: string | null;
  setReceiptCategoryWarning: React.Dispatch<React.SetStateAction<string | null>>;
  receiptCategorySeverity: 'blocking' | 'advisory' | null;
  setReceiptCategorySeverity: React.Dispatch<React.SetStateAction<'blocking' | 'advisory' | null>>;
  /** AI-vs-selection categorization disagreement (only set when the
   *  categorization is locked — the AI's detected type is kept as a flag,
   *  never applied). Persisted on the DIN for DocumentModal review. */
  aiCategoryDisagreement: AiCategoryDisagreement | null;
  setAiCategoryDisagreement: React.Dispatch<React.SetStateAction<AiCategoryDisagreement | null>>;
  aiSupplierMatch: { contact_id: number; contact_name: string; confidence: number; match_method: string } | null;
  setAiSupplierMatch: React.Dispatch<React.SetStateAction<{ contact_id: number; contact_name: string; confidence: number; match_method: string } | null>>;
  aiSuggestingSupplier: boolean;
  setAiSuggestingSupplier: React.Dispatch<React.SetStateAction<boolean>>;
  duplicateWarning: DuplicateMatch[] | null;
  setDuplicateWarning: React.Dispatch<React.SetStateAction<DuplicateMatch[] | null>>;
  duplicateChecking: boolean;
  setDuplicateChecking: React.Dispatch<React.SetStateAction<boolean>>;

  // Intra-receipt duplicate line items (from overlapping multi-photo capture)
  lineItemDuplicates: DetectDuplicateLineItemsResult;
  /** Remove all flagged duplicate line items (keeps first occurrence of each). */
  removeDuplicateLineItems: () => void;
  /** Dismiss the intra-receipt duplicate warning without removing items. */
  dismissLineItemDuplicates: () => void;

  // Auto-switch banner
  autoSwitchBanner: AutoSwitchBanner | null;
  dismissAutoSwitchBanner: () => void;
}

/**
 * Map AI document_type and suggested_category to a ChildEntryType.
 * Returns null if no switch is needed or confidence is too low.
 * - Procurement types use document_type (delivery_note, supplier_invoice, etc.)
 * - Vehicle expense types use suggested_category (fuel, toll, maintenance)
 * - Generic 'purchase' uses both signals
 */
function mapAiDocumentTypeToEntryType(
  aiDocumentType: string | null,
  aiSuggestedCategory: string | null,
  currentEntryType: ChildEntryType | null,
  aiConfidence: number,
): ChildEntryType | null {
  if (aiConfidence < 0.75) return null;

  const docType = String(aiDocumentType || '').trim().toLowerCase();
  const category = String(aiSuggestedCategory || '').trim().toLowerCase();

  const procurementMap: Record<string, ChildEntryType> = {
    delivery_note: 'delivery_note',
    supplier_invoice: 'supplier_invoice',
    tax_invoice: 'supplier_invoice',
    invoice: 'supplier_invoice',
    statement: 'supplier_invoice',
    quote: 'supplier_quote',
    supplier_quote: 'supplier_quote',
    quotation: 'supplier_quote',
    estimate: 'supplier_quote',
    proposal: 'supplier_quote',
  };

  const vehicleMap: Record<string, ChildEntryType> = {
    fuel: 'fuel',
    toll: 'toll',
    maintenance: 'maintenance',
    roadside: 'maintenance',
    parts: 'maintenance',
  };

  const isCurrentProcurement =
    currentEntryType === 'supplier_invoice' ||
    currentEntryType === 'supplier_quote' ||
    currentEntryType === 'delivery_note' ||
    currentEntryType === 'grn' ||
    currentEntryType === 'purchase_return' ||
    currentEntryType === 'supplier_credit';
  const isCurrentVehicle =
    currentEntryType === 'fuel' ||
    currentEntryType === 'toll' ||
    currentEntryType === 'maintenance';

  if (isCurrentProcurement && procurementMap[docType]) {
    return procurementMap[docType] !== currentEntryType ? procurementMap[docType] : null;
  }

  if (isCurrentVehicle && vehicleMap[category]) {
    return vehicleMap[category] !== currentEntryType ? vehicleMap[category] : null;
  }

  if (currentEntryType === 'purchase') {
    if (procurementMap[docType]) return procurementMap[docType];
    if (vehicleMap[category]) return vehicleMap[category];
  }

  return null;
}

/** Stable empty result for the intra-receipt duplicate detection state. */
const EMPTY_DUPLICATE_RESULT: DetectDuplicateLineItemsResult = {
  groups: [],
  duplicateIndexSet: new Set<number>(),
  duplicateCount: 0,
};

/**
 * Normalize an AI-extracted receipt date into `YYYY-MM-DDTHH:mm` (or a bare
 * `YYYY-MM-DD` when the receipt carries no time). Never fabricates a time —
 * a missing time means the user must pick the slip time in the form, which
 * keeps the transaction time trustworthy for duplicate detection.
 *
 * Accepts:
 *  - `YYYY-MM-DD`             → date only (time left for the user to pick)
 *  - `YYYY-MM-DD HH:mm[:ss]`  → replaces the space with `T` and trims seconds
 *  - `YYYY-MM-DDTHH:mm[:ss]`  → trims seconds
 *  - `…[T ]00:00[:00]`        → treated as "no time" (OCR default for missing time)
 *  - Any other parseable date → date + time only if the raw text had one
 * Returns null when the input cannot be parsed.
 */
function toDatetimeLocalValue(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Normalize single-digit hours from OCR (e.g. "2024-09-11T7:55:00") so the
  // strict two-digit patterns below can match. Without this the input falls
  // through to the date-only branch and the real receipt time is silently
  // replaced with the current capture time.
  const normalized = trimmed.replace(/([T ])(\d):(\d{2})/, '$10$2:$3');

  // Already datetime-local (with optional seconds)
  const dtMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::\d{2})?/);
  if (dtMatch) {
    return dtMatch[2] === '00:00' ? dtMatch[1] : `${dtMatch[1]}T${dtMatch[2]}`;
  }

  // Date-only YYYY-MM-DD → return as-is; the user picks the slip time
  const dateOnlyMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (dateOnlyMatch) {
    return dateOnlyMatch[1];
  }

  // Try generic Date parsing as a last resort
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  const yyyy = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hh = String(parsed.getHours()).padStart(2, '0');
  const mm = String(parsed.getMinutes()).padStart(2, '0');
  // Only keep the parsed time when the raw text actually contained one —
  // Date parsing defaults to midnight otherwise.
  const hasTime = /[T ]\d{1,2}:\d{2}/.test(normalized);
  if (!hasTime || (hh === '00' && mm === '00')) return `${yyyy}-${month}-${day}`;
  return `${yyyy}-${month}-${day}T${hh}:${mm}`;
}

export function useReceiptAnalysis(params: UseReceiptAnalysisParams): UseReceiptAnalysisReturn {
  const {
    formData,
    setFormData,
    setReceiptResult,
    setReceiptValidation,
    detectedCurrency,
    setExpenseLineItems,
    selectedEntryType,
    isProcurementType,
    selectedSupplier,
    setSelectedSupplier,
    selectedPurchaseOrder,
    poLineItems,
    allSuppliers,
    isOnline,
    preferredDefaultTaxRate,
    effectiveVehicleRegistration,
    linkableWorkItemLocation,
    captureProfile,
    configFields,
    canSwitchEntryTypeInCamera,
    suppressAutoCategorize = false,
    onAutoSwitchEntryType,
    publicUploadToken,
  } = params;

  // ── UI state ────────────────────────────────────────────────────────
  const [receiptValidating, setReceiptValidating] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [receiptCategoryWarning, setReceiptCategoryWarning] = useState<string | null>(null);
  const [receiptCategorySeverity, setReceiptCategorySeverity] = useState<'blocking' | 'advisory' | null>(null);
  const [aiCategoryDisagreement, setAiCategoryDisagreement] = useState<AiCategoryDisagreement | null>(null);
  const [aiSupplierMatch, setAiSupplierMatch] = useState<{ contact_id: number; contact_name: string; confidence: number; match_method: string } | null>(null);
  const [aiSuggestingSupplier, setAiSuggestingSupplier] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateMatch[] | null>(null);
  const [duplicateChecking, setDuplicateChecking] = useState(false);
  const [autoSwitchBanner, setAutoSwitchBanner] = useState<AutoSwitchBanner | null>(null);

  // ── Intra-receipt duplicate line items (overlapping multi-photo capture) ──
  // When a long till slip is photographed in overlapping segments, the AI
  // extraction can return the overlapping line items twice. We detect those
  // duplicates here so the UI can flag them for review/removal.
  const [lineItemDuplicates, setLineItemDuplicates] = useState<DetectDuplicateLineItemsResult>(EMPTY_DUPLICATE_RESULT);
  const [lineItemDuplicatesDismissed, setLineItemDuplicatesDismissed] = useState(false);
  // Last receipt + contact the duplicate check ran against, plus a signature
  // of the checked inputs — used to re-check when the user edits the
  // transaction date/time or receipt number after analysis.
  const lastDupCheckInputRef = useRef<{ receipt: ReceiptAiPayload; contactId?: number } | null>(null);
  const lastDupSigRef = useRef('');

  const removeDuplicateLineItems = useCallback(() => {
    setExpenseLineItems((prev) => deduplicateLineItems(prev, lineItemDuplicates.duplicateIndexSet));
    setLineItemDuplicates(EMPTY_DUPLICATE_RESULT);
    setLineItemDuplicatesDismissed(false);
  }, [lineItemDuplicates.duplicateIndexSet, setExpenseLineItems]);

  const dismissLineItemDuplicates = useCallback(() => {
    setLineItemDuplicatesDismissed(true);
  }, []);

  const dismissAutoSwitchBanner = useCallback(() => setAutoSwitchBanner(null), []);

  // ── Receipt AI analysis (supports multi-page) ───────────────────────
  const runReceiptAnalysis = useCallback(async (images: string[]) => {
    if (images.length === 0) return;

    // Casual wages use the photo as proof of authorization, not as a receipt for AI extraction.
    if (selectedEntryType === 'casual_wage') {
      setReceiptValidating(false);
      setReceiptError(null);
      return;
    }

    if (!isOnline) {
      setReceiptValidating(false);
      setReceiptError(null);
      setReceiptResult(null);
      setReceiptValidation(null);
      return;
    }

    setReceiptValidating(true);
    setReceiptError(null);
    setReceiptResult(null);
    setReceiptValidation(null);
    setAiCategoryDisagreement(null);
    // Reset intra-receipt duplicate state for the new analysis run
    setLineItemDuplicates(EMPTY_DUPLICATE_RESULT);
    setLineItemDuplicatesDismissed(false);

    try {
      const formDataRecord = toRecordString(formData);
      const declaredAmount = formDataRecord.amount ? Number(formDataRecord.amount) : undefined;
      const poContext = isProcurementType && selectedPurchaseOrder && selectedSupplier && poLineItems.length > 0
        ? {
          purchase_order_id: selectedPurchaseOrder.id,
          purchase_order_number: selectedPurchaseOrder.item_number,
          supplier_name: selectedSupplier.company_name || selectedSupplier.name,
          line_items: poLineItems,
        }
        : undefined;

      // Pass supplier context for delivery note / quote validation
      const supplierContext = isProcurementType && selectedSupplier
        ? {
          supplier_id: selectedSupplier.id,
          supplier_name: selectedSupplier.company_name || selectedSupplier.name,
          is_delivery_note: selectedEntryType === 'delivery_note' || selectedEntryType === 'grn',
          is_quote: selectedEntryType === 'supplier_quote',
        }
        : undefined;

      const analyzed = await analyzeReceiptImages({
        images,
        declared_amount: declaredAmount,
        entry_type: selectedEntryType || 'purchase',
        capture_profile: captureProfile,
        po_context: poContext,
        supplier_context: supplierContext,
        appointment_destination: linkableWorkItemLocation || undefined,
        vehicle_registration: effectiveVehicleRegistration || undefined,
        publicUploadToken,
      });

      if (!analyzed.success) {
        setReceiptError(
          `${analyzed.error || 'Receipt analysis service is temporarily unavailable'}. You can continue and capture the details manually.`,
        );
        return;
      }

      setReceiptResult(analyzed.receipt);
      setReceiptValidation(analyzed.validation);

      // Require user acknowledgment when major PO/receipt discrepancies are detected
      if (analyzed.receipt.discrepancy_notes) {
        const acknowledged = await notify.confirm({
          title: 'Receipt Discrepancies Detected',
          message: `The AI detected discrepancies between this receipt and the expected purchase order:\n\n${analyzed.receipt.discrepancy_notes}\n\nDo you acknowledge these discrepancies and wish to proceed?`,
          confirmText: 'Acknowledge & Proceed',
          cancelText: 'Review Details',
          requireConfirmation: {
            type: 'checkbox',
            label: 'I acknowledge the discrepancies and have reviewed the details',
          },
        });
        if (!acknowledged) {
          notify.info('Please review the receipt details before submitting.', 5000, 'smarttask-receipt-discrepancy');
        }
      }

      // AI auto-detection: compare the detected type against the selection.
      // The detected type is always computed so a disagreement can be flagged
      // even when the categorization is locked.
      const aiDetectedType = (analyzed.receipt?.document_type || analyzed.receipt?.suggested_category)
        ? mapAiDocumentTypeToEntryType(
          analyzed.receipt.document_type,
          analyzed.receipt.suggested_category,
          selectedEntryType,
          analyzed.receipt.confidence ?? 0,
        )
        : null;

      // Finance users (canSwitchEntryTypeInCamera) can auto-switch to any detected type.
      // Zero-trust field workers can auto-switch to vehicle expense types (fuel, toll, maintenance)
      // so that vehicle linkage and odometer capture activate automatically from the receipt AI.
      // Never fires when suppressAutoCategorize — a user-selected categorization
      // (any of the 6 linking modes, vehicle lock, job-linked expense) is
      // authoritative and must never be overridden by the AI.
      let didAutoSwitch = false;
      if (!suppressAutoCategorize && aiDetectedType && aiDetectedType !== selectedEntryType) {
        const isVehicleType = ['fuel', 'toll', 'maintenance'].includes(aiDetectedType);
        if (canSwitchEntryTypeInCamera || isVehicleType) {
          const detectedLabel = ENTRY_CONFIG[aiDetectedType]?.label || aiDetectedType;
          setAutoSwitchBanner({
            detectedType: aiDetectedType,
            detectedLabel,
            originalType: selectedEntryType || 'purchase',
          });
          onAutoSwitchEntryType?.(aiDetectedType);
          didAutoSwitch = true;
        }
      }

      // Check for category mismatch (e.g. fuel receipt that looks like food)
      // Skip if we already auto-switched — the type has been corrected
      if (!didAutoSwitch) {
        const mismatch = getReceiptCategoryMismatchInfo(selectedEntryType || 'purchase', analyzed.receipt ? {
          suggested_category: analyzed.receipt.suggested_category ?? undefined,
          confidence: analyzed.receipt.confidence ?? undefined,
        } : null);
        if (suppressAutoCategorize) {
          // Locked categorization: the technician's selection is authoritative.
          // Record the disagreement so it lands on the DIN for DocumentModal /
          // ExpenseInbox review — show a non-blocking advisory, never switch
          // the entry type and never block the save.
          // A supplier-document detection (e.g. the AI read "TAX INVOICE" on
          // a slip) is only a real disagreement when the extracted vendor
          // cross-references a supplier the client knows. With no matching
          // supplier the "supplier invoice" guess is unfounded — it stays a
          // general expense and is not flagged.
          const ocrVendorName = analyzed.receipt?.vendor_name?.trim();
          const supplierDocDetection = aiDetectedType !== null && isSupplierDocumentEntryType(aiDetectedType);
          const vendorMatchesKnownSupplier = !!ocrVendorName && allSuppliers.some((s) =>
            [s.company_name, s.name].some((n) => !!n?.trim() && supplierNamesMatch(ocrVendorName, n)));
          const typeDisagrees =
            aiDetectedType !== null &&
            aiDetectedType !== selectedEntryType &&
            !(supplierDocDetection && !vendorMatchesKnownSupplier);
          if (typeDisagrees || mismatch.message) {
            const detectedLabel = typeDisagrees
              ? (ENTRY_CONFIG[aiDetectedType]?.label || aiDetectedType)
              : null;
            const aiLabel = detectedLabel
              || analyzed.receipt?.suggested_category
              || analyzed.receipt?.document_type
              || 'a different category';
            const advisory = `AI detected this as ${aiLabel} — your selected category was kept and will be flagged for review.`;
            setAiCategoryDisagreement({
              selectedType: selectedEntryType,
              detectedType: typeDisagrees ? aiDetectedType : null,
              detectedLabel,
              suggestedCategory: analyzed.receipt?.suggested_category ?? null,
              documentType: analyzed.receipt?.document_type ?? null,
              confidence: analyzed.receipt?.confidence ?? null,
            });
            setReceiptCategoryWarning(advisory);
            setReceiptCategorySeverity('advisory');
            notify.warning(advisory, 6000, 'smarttask-receipt-category-mismatch');
          } else {
            setAiCategoryDisagreement(null);
            setReceiptCategoryWarning(null);
            setReceiptCategorySeverity(null);
          }
        } else {
          setReceiptCategoryWarning(mismatch.message);
          setReceiptCategorySeverity(mismatch.severity);
          if (mismatch.message) {
            notify.warning(mismatch.message, 6000, 'smarttask-receipt-category-mismatch');
          }
        }
      } else {
        setAiCategoryDisagreement(null);
        setReceiptCategoryWarning(null);
        setReceiptCategorySeverity(null);
      }

      // Auto-populate form fields from AI extraction
      setFormData((prev) => {
        const prevRecord = toRecordString(prev);
        const updates: Record<string, string> = {};
        const rec = analyzed.receipt;

        // Only auto-fill amount for non-delivery-note types
        if (rec.total_amount != null && !prevRecord.amount && selectedEntryType !== 'delivery_note' && selectedEntryType !== 'grn') {
          updates.amount = String(rec.total_amount);
        }

        // Auto-fill transaction date (& time) from receipt_date.
        // Normalizes to YYYY-MM-DDTHH:mm for the split date/time pickers.
        // If the AI only extracted a date, the time is deliberately left
        // empty — the user must pick the slip time so the datetime is
        // trustworthy for duplicate detection.
        if (rec.receipt_date && !prevRecord.date) {
          const normalized = toDatetimeLocalValue(rec.receipt_date);
          if (normalized) {
            updates.date = normalized;
          }
        }

        // Only auto-fill vendor if supplier not already selected
        if (rec.vendor_name && !selectedSupplier) {
          if (!prevRecord.station && selectedEntryType === 'fuel') updates.station = rec.vendor_name;
          if (!prevRecord.vendor && selectedEntryType === 'purchase') updates.vendor = rec.vendor_name;
          if (!prevRecord.toll_name && selectedEntryType === 'toll') updates.toll_name = rec.vendor_name;
        }

        if (rec.receipt_number) {
          if (!prevRecord.receipt_number) updates.receipt_number = rec.receipt_number;
          if (!prevRecord.invoice_number && selectedEntryType === 'supplier_invoice') updates.invoice_number = rec.receipt_number;
          if (!prevRecord.delivery_number && selectedEntryType === 'delivery_note') updates.delivery_number = rec.receipt_number;
          if (!prevRecord.quote_number && selectedEntryType === 'supplier_quote') updates.quote_number = rec.receipt_number;
          if (!prevRecord.grn_number && selectedEntryType === 'grn') updates.grn_number = rec.receipt_number;
          if (!prevRecord.return_number && selectedEntryType === 'purchase_return') updates.return_number = rec.receipt_number;
        }

        const normalizedPaymentMethod = normalizeReceiptPaymentMethod(
          rec.suggested_payment_method || rec.payment_method,
          configFields.find((field) => field.key === 'payment_method')?.options,
        );
        if (normalizedPaymentMethod && !prevRecord.payment_method) {
          updates.payment_method = normalizedPaymentMethod;
        }
        if (rec.suggested_description && !prevRecord.description && (selectedEntryType === 'purchase' || selectedEntryType === 'maintenance')) {
          updates.description = rec.suggested_description;
        }
        if (rec.suggested_category && selectedEntryType === 'purchase' && !prevRecord.category) {
          const matchedCategory = matchReceiptSuggestionOption(rec.suggested_category, PURCHASE_CATEGORY_OPTIONS);
          if (matchedCategory) {
            updates.category = matchedCategory;
          }
        }
        if (rec.vendor_name && selectedEntryType === 'maintenance' && !prevRecord.description && !updates.description) {
          updates.description = `Maintenance at ${rec.vendor_name}`;
        }
        // Auto-populate odometer from AI extraction (fuel/maintenance receipts)
        if (rec.odometer != null && !prevRecord.odometer && (selectedEntryType === 'fuel' || selectedEntryType === 'maintenance')) {
          updates.odometer = String(rec.odometer);
        }
        // Auto-populate litres from AI extraction (fuel receipts only)
        if (rec.litres != null && rec.litres > 0 && !prevRecord.litres && selectedEntryType === 'fuel') {
          updates.litres = String(rec.litres);
        }
        // Auto-populate fuel type from AI extraction (fuel receipts only)
        if (rec.fuel_type && !prevRecord.fuel_type && selectedEntryType === 'fuel') {
          updates.fuel_type = rec.fuel_type;
        }
        if (rec.payment_method && !prevRecord.payment_method && !normalizedPaymentMethod) {
          const pm = rec.payment_method.charAt(0).toUpperCase() + rec.payment_method.slice(1).toLowerCase();
          updates.payment_method = pm;
        }
        return { ...prev, ...updates } as CaptureFormData;
      });

      // AI-driven supplier match — capture-first flow (all expense types)
      // Skip in public mode: the ai-suggestions endpoint requires user auth.
      // Skipped in suppressAutoCategorize (General Expense) mode — a general
      // expense must not be silently linked to a supplier the user never
      // picked (that link also triggers the payment-terms supplier_invoice
      // auto-switch, which would re-categorize the expense).
      let matchedContactId: number | undefined = undefined;
      if (analyzed.receipt?.vendor_name && !selectedSupplier && !publicUploadToken && !suppressAutoCategorize) {
        setAiSuggestingSupplier(true);
        setAiSupplierMatch(null);
        try {
          const token = authService.getTokens()?.access_token;
          const aiRes = await fetch('/api/v1/finance/ai-suggestions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              vendor_name: analyzed.receipt.vendor_name,
              entry_type: selectedEntryType,
              total_amount: analyzed.receipt.total_amount,
              line_items: analyzed.receipt.line_items,
            }),
          });
          if (aiRes.ok) {
            const aiData = await aiRes.json();
            if (aiData.success && aiData.supplierMatch && aiData.supplierMatch.contact_id) {
              setAiSupplierMatch(aiData.supplierMatch);
              matchedContactId = aiData.supplierMatch.contact_id;
              const matched = allSuppliers.find(s => s.id === aiData.supplierMatch.contact_id);
              if (matched) {
                setSelectedSupplier(matched);
              }
            }
          }
        } catch (aiErr) {
          if (!isSilentFetchError(aiErr)) logger.error('[ChildEntryForm] AI supplier match failed:', aiErr);
        } finally {
          setAiSuggestingSupplier(false);
        }
      } else if (selectedSupplier?.id) {
        matchedContactId = selectedSupplier.id;
      }

      // Auto-populate expense line items from AI extraction for all expense types
      if (EXPENSE_ENTRY_TYPES.includes(selectedEntryType as ChildEntryType)) {
        if (analyzed.receipt?.line_items && analyzed.receipt.line_items.length > 0) {
          const receiptCurrency = analyzed.receipt?.currency || detectedCurrency;

          // Use detectVatTreatment to determine the seller's tax setup:
          // - Whether VAT is charged at all (fuel = 0%, toll = 0%)
          // - Whether prices are VAT-inclusive or exclusive
          // - The correct tax rate
          const vatTreatment = detectVatTreatment({
            selectedEntryType,
            receiptResult: analyzed.receipt,
            detectedCurrency: receiptCurrency,
            vendorName: analyzed.receipt?.vendor_name,
            preferredDefaultTaxRate: preferredDefaultTaxRate ? { rate: String(preferredDefaultTaxRate.rate) } : null,
          });
          const inferredVatRate = vatTreatment.rate;
          const inferredTaxInclusive = vatTreatment.taxInclusive;

          const receiptSubtotal = analyzed.receipt.subtotal != null ? Number(analyzed.receipt.subtotal) : null;
          const receiptTaxAmount = analyzed.receipt.tax_amount != null ? Number(analyzed.receipt.tax_amount) : null;
          const receiptTotalAmount = analyzed.receipt.total_amount != null ? Number(analyzed.receipt.total_amount) : null;

          const extractedLineItems: DocumentLineItem[] = analyzed.receipt.line_items.map((item, idx) => {
            const qty = Number(item.quantity) || 1;
            const price = item.unit_price ? Number(item.unit_price) : 0;
            const lineTotal = item.total != null ? Number(item.total) : (qty * price);
            const taxRate = (item.tax_rate != null && Number(item.tax_rate) > 0) ? Number(item.tax_rate) : inferredVatRate;
            const taxInclusive = inferredTaxInclusive;

            // Compute tax_amount and line_total based on VAT treatment.
            // IMPORTANT: line_total must always be the GROSS amount (what the
            // customer paid). When tax_inclusive, the OCR price is already
            // gross, so line_total = lineTotal and tax_amount is extracted
            // from it. When tax_exclusive, tax is added on top to get gross.
            let roundedTaxAmount: number;
            let computedLineTotal: number;

            if (taxRate > 0 && taxInclusive) {
              // Price includes VAT — line_total is the gross amount as-is.
              // Extract the tax component from the gross for tax_amount.
              const taxDivisor = 1 + taxRate / 100;
              roundedTaxAmount = Math.round((lineTotal - (lineTotal / taxDivisor)) * 100) / 100;
              computedLineTotal = Math.round(lineTotal * 100) / 100;
            } else if (taxRate > 0) {
              // Price excludes VAT — tax is added on top to get gross
              roundedTaxAmount = Math.round((lineTotal * (taxRate / 100)) * 100) / 100;
              computedLineTotal = Math.round((lineTotal + roundedTaxAmount) * 100) / 100;
            } else {
              // No tax
              roundedTaxAmount = 0;
              computedLineTotal = Math.round(lineTotal * 100) / 100;
            }

            if (idx === 0 && analyzed.receipt.line_items.length === 1 && receiptTotalAmount != null) {
              computedLineTotal = Math.round(receiptTotalAmount * 100) / 100;
            }

            return {
              description: item.description || '',
              quantity: qty,
              unit_price: price,
              line_total: computedLineTotal,
              tax_rate: taxRate,
              tax_amount: roundedTaxAmount,
              tax_inclusive: taxInclusive,
              sort_order: idx + 1,
              line_type: 'item' as const,
              unit: 'each',
            };
          });
          setExpenseLineItems(extractedLineItems);

          // Detect intra-receipt duplicate line items. This commonly happens
          // when a long till slip / multi-page A4 transaction is captured as
          // several overlapping photos — the overlap region is extracted
          // twice. Flag the duplicates so the UI can prompt the user to
          // review/remove them before submission.
          const dupResult = detectDuplicateLineItems(extractedLineItems);
          if (dupResult.duplicateCount > 0) {
            setLineItemDuplicates(dupResult);
            setLineItemDuplicatesDismissed(false);
            logger.info(
              '[useReceiptAnalysis] Detected %d duplicate line item(s) from overlapping photos',
              dupResult.duplicateCount,
            );
          }
        } else if ((selectedEntryType === 'delivery_note' || selectedEntryType === 'grn') && poLineItems.length > 0) {
          // Delivery note: fall back to PO line items if AI extraction fails
          setExpenseLineItems((currentItems) => {
            if (currentItems.length === 0) {
              return poLineItems.map((item, idx) => ({
                description: item.description,
                quantity: item.quantity,
                unit_price: item.unit_price || 0,
                line_type: 'item' as const,
                sort_order: idx + 1,
                unit: 'each',
                tax_rate: 0,
                tax_amount: 0,
              })) as DocumentLineItem[];
            }
            return currentItems;
          });
        }
      }

      // ── Duplicate detection: check after AI extraction populates fields ──
      if (analyzed.receipt) {
        setDuplicateChecking(true);
        try {
          // Prefer the user-confirmed form values over the raw AI payload —
          // the user may have corrected the receipt number, and the form
          // date may already carry a picked slip time. The stale `formData`
          // predates the auto-fill above, so fall back to the normalized
          // AI date the form is about to show.
          const formRecord = toRecordString(formData);
          const dupExpenseDate = formRecord.date
            || toDatetimeLocalValue(analyzed.receipt.receipt_date)
            || null;
          const dupReceiptNumber = formRecord.receipt_number || formRecord.invoice_number || null;
          lastDupCheckInputRef.current = { receipt: analyzed.receipt, contactId: matchedContactId };
          lastDupSigRef.current = `${dupExpenseDate ?? ''}|${dupReceiptNumber ?? ''}|${matchedContactId ?? ''}`;
          const dupResult = await checkDuplicateEvidence(analyzed.receipt, matchedContactId, {
            expenseDate: dupExpenseDate,
            receiptNumber: dupReceiptNumber,
          });
          setDuplicateWarning(dupResult);
        } catch (dupErr) {
          if (!isSilentFetchError(dupErr)) logger.error('[ChildEntryForm] Duplicate check error:', dupErr);
        } finally {
          setDuplicateChecking(false);
        }
      }
    } catch (err: unknown) {
      if (!isSilentFetchError(err)) logger.error('[ChildEntryForm] Receipt validation error:', err);
      setReceiptError('Receipt validation service is temporarily unavailable. You can continue and capture the details manually.');
    } finally {
      setReceiptValidating(false);
    }
  }, [captureProfile, configFields, selectedEntryType, formData, isOnline, isProcurementType, selectedPurchaseOrder, selectedSupplier, setSelectedSupplier, poLineItems, linkableWorkItemLocation, allSuppliers, preferredDefaultTaxRate, detectedCurrency, effectiveVehicleRegistration, onAutoSwitchEntryType, canSwitchEntryTypeInCamera, suppressAutoCategorize, publicUploadToken, setFormData, setReceiptResult, setReceiptValidation, setExpenseLineItems, setReceiptValidating, setReceiptError, setReceiptCategoryWarning, setReceiptCategorySeverity, setAiCategoryDisagreement, setAiSuggestingSupplier, setAiSupplierMatch, setDuplicateChecking, setDuplicateWarning, setLineItemDuplicates, setLineItemDuplicatesDismissed]);

  // Re-run the duplicate check when the user edits the fields the check keys
  // on — most importantly the slip date/time they pick when OCR couldn't read
  // it (the analysis-time check runs before that user input exists).
  // Debounced so picker selections settle before the API call.
  const dupWatchRecord = toRecordString(formData);
  const dupWatchDate = dupWatchRecord.date || '';
  const dupWatchReceiptNo = dupWatchRecord.receipt_number || dupWatchRecord.invoice_number || '';
  useEffect(() => {
    const input = lastDupCheckInputRef.current;
    if (!input || !dupWatchDate) return;
    const contactId = selectedSupplier?.id ?? input.contactId;
    const sig = `${dupWatchDate}|${dupWatchReceiptNo}|${contactId ?? ''}`;
    if (sig === lastDupSigRef.current) return;
    const timer = setTimeout(() => {
      void (async () => {
        setDuplicateChecking(true);
        try {
          const result = await checkDuplicateEvidence(input.receipt, contactId, {
            expenseDate: dupWatchDate,
            receiptNumber: dupWatchReceiptNo || null,
          });
          lastDupSigRef.current = sig;
          setDuplicateWarning(result);
        } catch (err) {
          if (!isSilentFetchError(err)) logger.error('[useReceiptAnalysis] Duplicate re-check error:', err);
        } finally {
          setDuplicateChecking(false);
        }
      })();
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and setters are stable; the watched form values are the meaningful deps
  }, [dupWatchDate, dupWatchReceiptNo, selectedSupplier?.id]);

  // Effective duplicate state — hidden once the user dismisses the warning
  const effectiveLineItemDuplicates: DetectDuplicateLineItemsResult =
    lineItemDuplicatesDismissed ? EMPTY_DUPLICATE_RESULT : lineItemDuplicates;

  return {
    runReceiptAnalysis,
    receiptValidating,
    setReceiptValidating,
    receiptError,
    setReceiptError,
    receiptCategoryWarning,
    setReceiptCategoryWarning,
    receiptCategorySeverity,
    setReceiptCategorySeverity,
    aiCategoryDisagreement,
    setAiCategoryDisagreement,
    aiSupplierMatch,
    setAiSupplierMatch,
    aiSuggestingSupplier,
    setAiSuggestingSupplier,
    duplicateWarning,
    setDuplicateWarning,
    duplicateChecking,
    setDuplicateChecking,
    lineItemDuplicates: effectiveLineItemDuplicates,
    removeDuplicateLineItems,
    dismissLineItemDuplicates,
    autoSwitchBanner,
    dismissAutoSwitchBanner,
  };
}
